import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AuthApiService } from '@orderna/admin-panel/src/app/core/auth/auth-api.service';
import { TransactionStatus } from '@orderna/admin-panel/src/app/model/enum/transaction-status';
import { CurrentTransactionService } from '@orderna/admin-panel/src/app/services/shared/current-transaction/current-transaction.service';
import { OfflineService } from '@orderna/admin-panel/src/app/services/shared/offline/offline.service';
import { PosOfflineDbService } from '@orderna/admin-panel/src/app/services/shared/offline/pos-offline-db.service';
import { BadgeComponent } from '@orderna/admin-panel/src/app/shared/components/badge/badge.component';
import { TransactionsApiService } from '../../../../../services/transactions/transactions-api.service';
import {
  ButtonComponent,
  ButtonTextDirective,
} from '../../../../../shared/components/button/button.component';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SortByTransactionField } from '@orderna/admin-panel/src/app/services/transactions/enums/sort-by-transaction-field.enum';
import { SortDirectionDto } from '@orderna/admin-panel/src/app/shared/dto/sort-direction.dto';
import { FindTransactionsResponseDto } from '@orderna/admin-panel/src/app/services/transactions/dtos/find-transactions.response.dto';
import { Metadata } from '@orderna/admin-panel/src/app/model/metadata';
import { ErrorListComponent } from '@orderna/admin-panel/src/app/shared/components/modal/error-list/error-list.component';
import { FindTransactionDetailsResponseDto } from '@orderna/admin-panel/src/app/services/transactions/dtos/find-transaction-details.response.dto';
import { ApplicableDiscount } from '@orderna/admin-panel/src/app/model/discount-applicable';
import { CustomerStatutoryDiscountType } from '@orderna/admin-panel/src/app/services/customers/enums/customer-statutory-discount-type';
import { HttpErrorResponse } from '@angular/common/http';
import { closePointOfSalesModal } from '../../utils/close-point-of-sales-modal';
import { PosCatalogService } from '@orderna/admin-panel/src/app/services/shared/pos-catalog/pos-catalog.service';
import {
  CurrentTransactionBundleOption,
  CurrentTransactionItem,
} from '@orderna/admin-panel/src/app/services/shared/current-transaction/types/current-transaction';
import { CurrentDeviceService } from '@orderna/admin-panel/src/app/services/shared/current-device/current-device.service';

@Component({
  selector: 'app-park-transaction-modal',
  standalone: true,
  templateUrl: './park-transaction-modal.component.html',
  styleUrls: [
    '/src/app/shared/components/modal/modal.component.scss',
    './park-transaction-modal.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    ButtonComponent,
    ButtonTextDirective,
    ReactiveFormsModule,
    PaginationComponent,
    BadgeComponent,
    ErrorListComponent,
  ],
})
export class ParkTransactionModalComponent implements OnInit {
  readonly PAGE_SIZE = 5;

  #dialogRef = inject(MatDialogRef<ParkTransactionModalComponent>);
  #transactionApi = inject(TransactionsApiService);
  #currentTransaction = inject(CurrentTransactionService);
  #currentDevice = inject(CurrentDeviceService);
  #auth = inject(AuthApiService);
  #offlineService = inject(OfflineService);
  #offlineDb = inject(PosOfflineDbService);

  #posCatalog = inject(PosCatalogService);

  loading = signal(false);
  errorMessages = signal<string[]>([]);
  selectedUuid = signal<string | null>(null);
  parkedTransactions = signal<FindTransactionsResponseDto['data'][0][]>([]);
  meta = signal<Metadata>({
    page: 1,
    size: this.PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  async ngOnInit() {
    this.loading.set(true);
    try {
      if (!this.#currentTransaction.empty()) {
        await this.parkCurrentTransaction();
      }

      await this.loadParkedTransactions();
      this.initSelectedUuid();
    } finally {
      this.loading.set(false);
    }
  }

  closeDialog() {
    closePointOfSalesModal(this.#dialogRef);
  }

  initSelectedUuid() {
    const firstParkedTxnUuid = this.parkedTransactions()[0]?.uuid ?? null;
    const selectedUuid = this.#currentTransaction.uuid() ?? firstParkedTxnUuid;
    this.selectedUuid.set(selectedUuid);
  }

  async loadParkedTransactions(page: number = 1) {
    const registerId = this.#currentDevice.registerId();
    const storeId = this.#currentDevice.storeId();
    if (!registerId || !storeId) {
      return;
    }

    const loadFromOfflineStorage = async () => {
      const offlineParked = await this.#offlineDb.getParkedTransactions(
        storeId,
        registerId,
      );

      const transformedData = offlineParked.map((p, index) => {
        const createdTime = p.transactionData?.createdAt || p.lastUpdated;
        return {
          id: index + 1,
          uuid: p.uuid,
          status: TransactionStatus.PARKED,
          timestamp: new Date(createdTime).toISOString(),
          customer: p.transactionData?.customer,
          totals: this.calculateOfflineTotals(p.transactionData),
        };
      });

      transformedData.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );

      const start = (page - 1) * this.PAGE_SIZE;
      const paginatedData = transformedData.slice(
        start,
        start + this.PAGE_SIZE,
      );

      this.parkedTransactions.set(paginatedData as any);
      this.meta.set({
        page,
        size: this.PAGE_SIZE,
        total: transformedData.length,
        totalPages: Math.ceil(transformedData.length / this.PAGE_SIZE),
      });
    };

    if (this.#offlineService.isOffline()) {
      try {
        await loadFromOfflineStorage();
      } catch (error) {
        console.error('Failed to load offline parked transactions:', error);
        this.errorMessages.set(['Failed to load parked transactions.']);
      }
      return;
    }

    try {
      const { meta, data } = await this.#transactionApi.findTransactions({
        status: [TransactionStatus.PARKED],
        registerId: registerId,
        staffId: this.#auth.currentUserId(),
        sortOrder: SortDirectionDto.DESC,
        sortBy: SortByTransactionField.TIMESTAMP,
        size: this.meta().size,
        page: page,
      });

      this.parkedTransactions.set(data);
      this.meta.set(meta);
    } catch (error) {
      console.error(
        'Failed to load parked transactions from API, trying offline storage:',
        error,
      );
      try {
        await loadFromOfflineStorage();
      } catch (offlineError) {
        console.error(
          'Failed to load offline parked transactions:',
          offlineError,
        );
        this.errorMessages.set(['Failed to load parked transactions.']);
      }
    }
  }

  private calculateOfflineTotals(transactionData: any): {
    grossSales: number;
    totalDiscount: number;
    netSales: number;
  } {
    if (!transactionData?.items) {
      return { grossSales: 0, totalDiscount: 0, netSales: 0 };
    }

    let grossSales = 0;
    let totalDiscount = 0;

    for (const [_, item] of transactionData.items) {
      if (item?.product?.price && item?.quantity) {
        grossSales += item.product.price * item.quantity;
        if (item.discount?.discountedAmount) {
          totalDiscount += item.discount.discountedAmount;
        }
      }
    }

    if (transactionData.discount?.discountedAmount) {
      totalDiscount += transactionData.discount.discountedAmount;
    }

    return { grossSales, totalDiscount, netSales: grossSales - totalDiscount };
  }

  async parkCurrentTransaction() {
    try {
      await this.#currentTransaction.park();
    } catch (error) {
      console.error('Failed to park current transaction:', error);
      this.errorMessages.set(
        (error as HttpErrorResponse).error.message ?? [
          'Failed to park current transaction.',
        ],
      );
    }
  }

  async retrieveTransaction() {
    this.loading.set(true);
    try {
      this.#currentTransaction.reset();

      const uuid = this.selectedUuid();
      if (!uuid) {
        this.errorMessages.set(['Failed to retrieve parked transaction.']);
        return;
      }

      const retrieveFromOffline = async (): Promise<boolean> => {
        const transactionData =
          await this.#offlineDb.getParkedTransaction(uuid);
        if (!transactionData) {
          return false;
        }

        this.setOfflineTransaction(uuid, transactionData);
        await this.#offlineDb.removeParkedTransaction(uuid);
        this.closeDialog();
        return true;
      };

      const isOfflineParked = uuid.startsWith('parked-');

      if (this.#offlineService.isOffline() || isOfflineParked) {
        try {
          const found = await retrieveFromOffline();
          if (!found) {
            this.errorMessages.set(['Parked transaction not found.']);
          }
        } catch (error) {
          console.error(
            'Failed to retrieve offline parked transaction:',
            error,
          );
          this.errorMessages.set(['Failed to retrieve parked transaction.']);
        }
        return;
      }

      try {
        const response =
          await this.#transactionApi.findTransactionDetails(uuid);
        this.setCurrentTransaction(response);
        this.closeDialog();
      } catch (error) {
        console.error(
          'Failed to retrieve parked transaction from API, trying offline:',
          error,
        );
        try {
          const found = await retrieveFromOffline();
          if (!found) {
            this.errorMessages.set(['Failed to retrieve parked transaction.']);
          }
        } catch (offlineError) {
          console.error(
            'Failed to retrieve offline parked transaction:',
            offlineError,
          );
          this.errorMessages.set(['Failed to retrieve parked transaction.']);
        }
      }
    } finally {
      this.loading.set(false);
    }
  }

  private async setOfflineTransaction(uuid: string, transactionData: any) {
    this.#currentTransaction.setTransactionUuid(uuid);
    this.#currentTransaction.setPaymentMethod(transactionData.paymentMethod);
    this.#currentTransaction.setTransactionStatus(TransactionStatus.PARKED);
    this.#currentTransaction.setTransactionNote(transactionData.note);

    if (transactionData.customer) {
      await this.#currentTransaction.setCustomer(transactionData.customer);
    }

    if (transactionData.discount) {
      await this.#currentTransaction.setTransactionDiscount(
        transactionData.discount,
      );
    }

    const currTxnItems: CurrentTransactionItem[] = [];
    if (transactionData.items) {
      for (const [_, item] of transactionData.items) {
        const product = this.#posCatalog
          .products()
          .find((p) => p.id === item.product.id);
        if (!product) continue;

        const { modifiers, ...rest } = product; // remove modifiers

        const currTxnItem = this.#currentTransaction.createItem({
          product: {
            ...rest,
            selectedModifierOptions: [], // TODO
          },
          quantity: item.quantity,
          note: item.note,
          promoItemDiscount: item.promoItemDiscount,
          promoTransactionDiscount: item.promoTransactionDiscount,
          statutoryDiscount: item.statutoryDiscount,
        });

        currTxnItems.push(currTxnItem);
      }
    }
    await this.#currentTransaction.setItems(currTxnItems);
  }

  async onPageChange(event: { currentPage: number }) {
    this.meta.update((pagination) => ({
      ...pagination,
      page: event.currentPage,
    }));
    this.loading.set(true);
    try {
      await this.loadParkedTransactions(this.meta().page);
    } finally {
      this.loading.set(false);
    }
  }

  async setCurrentTransaction(data: FindTransactionDetailsResponseDto) {
    this.#currentTransaction.setTransactionUuid(data.uuid);
    this.#currentTransaction.setPaymentMethod(data.paymentMethod);
    this.#currentTransaction.setTransactionStatus(TransactionStatus.PARKED);
    this.#currentTransaction.setTransactionNote(data.note);

    if (data.cashReceived) {
      this.#currentTransaction.setCashReceived(data.cashReceived);
    }

    if (data.customer) {
      const customer = data.customer;
      const statutoryDiscount = data.statutoryDiscount;

      await this.#currentTransaction.setCustomer({
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        fullName: `${customer.firstName} ${customer.lastName}`,
        statutoryDiscount:
          statutoryDiscount.type !== CustomerStatutoryDiscountType.NONE &&
          statutoryDiscount.status
            ? {
                type: statutoryDiscount.type,
                // FindTransactionDetailsReponseDto doesn't include statutory discount ID number
                // Would be okay not to set here, because idNumber doesn't affect transaction totals computation
                idNumber: '',
                status: statutoryDiscount.status,
              }
            : undefined,
      });
    }

    if (data.reference) {
      this.#currentTransaction.setReference(data.reference);
    }

    if (data.serviceType) {
      this.#currentTransaction.setServiceType(data.serviceType);
    }

    const currTxnItems: CurrentTransactionItem[] = [];
    for (const item of data.items) {
      const product = this.#posCatalog.productsById().get(item.product.id);
      if (!product) continue;

      const { modifiers, bundles, ...rest } = product;
      const allModifierOptions = modifiers.flatMap((m) => m.options);
      const selectedModifierOptionIds = new Set(
        item.modifierOptions.map((o) => o.modifierOptionId),
      );
      const filterSelectedModifiers = (options: typeof allModifierOptions) =>
        options.filter((option) => selectedModifierOptionIds.has(option.id));

      const allBundleOptions = bundles.flatMap((b) => b.options);
      const bundleOptionById = new Map(allBundleOptions.map((b) => [b.id, b]));

      const selectedBundleOptions: CurrentTransactionBundleOption[] = [];
      for (const {
        bundleOptionId,
        quantity,
        modifierOptions,
      } of item.bundleOptions) {
        const bundleOption = bundleOptionById.get(bundleOptionId);
        if (!bundleOption) continue;

        const bundleProduct = this.#posCatalog
          .productsById()
          .get(bundleOption.productId);
        if (!bundleProduct) continue;

        const allModifierOptions = bundleProduct.modifiers.flatMap(
          (m) => m.options,
        );
        const selectedModifierOptionIds = new Set(
          modifierOptions.map((o) => o.modifierOptionId),
        );
        const filterSelectedModifiers = (options: typeof allModifierOptions) =>
          options.filter((option) => selectedModifierOptionIds.has(option.id));

        selectedBundleOptions.push({
          ...bundleOption,
          productTitle: bundleProduct.title,
          quantity,
          selectedModifierOptions: filterSelectedModifiers(
            bundleProduct.modifiers.flatMap((m) => m.options),
          ),
        });
      }

      const currTxnItem = this.#currentTransaction.createItem({
        product: {
          ...rest,
          selectedModifierOptions: filterSelectedModifiers(allModifierOptions),
          selectedBundleOptions,
        },
        quantity: item.quantity,
      });

      currTxnItem.note = item.note;

      if (item.discount) {
        const { discounts, sales } = item.totals;
        const discount: ApplicableDiscount = {
          id: item.discount.id,
          code: item.discount.code,
          value: item.discount.value,
          type: item.discount.type,
          discountedAmount: discounts.promotional.item,
          amountAfterDiscount: sales.net,
        };
        currTxnItem.promoItemDiscount = discount;
      }
      currTxnItems.push(currTxnItem);
    }
    await this.#currentTransaction.setItems(currTxnItems);

    if (data.discount) {
      const { sales, discounts } = data.totals;
      const discount: ApplicableDiscount = {
        id: data.discount.id,
        code: data.discount.code,
        value: data.discount.value,
        type: data.discount.type,
        discountedAmount: discounts.promotional.transaction,
        amountAfterDiscount: sales.net,
      };
      await this.#currentTransaction.setTransactionDiscount(discount);
    }

    await this.#currentTransaction.setBnpcRemainingThisWeek(
      data.bnpcRemainingBalanceBeforeTxn,
    );
    // TODO: Set BNPC count purchased this week
  }
}
